/**
 * ====================================================================
 * FIKO CAD SMART MEASURE MODE
 * ====================================================================
 *
 * CATIA/SolidWorks "Smart Measure" ozelligine karsilik gelir.
 * Secime gore otomatik olcum tipi tespit eder ve uygun dimension olusturur.
 *
 * Kullanim:
 * ```typescript
 * const smartMeasure = new SmartMeasureMode(dimensionManager);
 *
 * // Tek secim - otomatik tip tespiti
 * smartMeasure.measureSingle(geometryRef);
 *
 * // Cift secim - otomatik mesafe tipi tespiti
 * smartMeasure.measureDouble(refA, refB);
 * ```
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import { DimensionManager } from './DimensionManager';
import {
    GeometryReference,
    ReferenceType,
    DimensionResult,
    DimensionType,
    AxisLock
} from './DimensionTypes';

// ====================================================================
// MEASUREMENT TYPE ENUMS
// ====================================================================

/**
 * Tek secim olcum tipleri (Kumpas modu)
 */
export enum SingleMeasurementType {
    COORDINATE = 'COORDINATE',           // Nokta koordinatlari
    LENGTH = 'LENGTH',                   // Kenar uzunlugu
    RADIUS = 'RADIUS',                   // Yaricap
    DIAMETER = 'DIAMETER',               // Cap
    ARC_LENGTH = 'ARC_LENGTH',           // Yay uzunlugu
    ARC_PROPERTIES = 'ARC_PROPERTIES',   // Yay (R + aci + uzunluk)
    AREA = 'AREA',                       // Yuzey alani
    SPHERE_RADIUS = 'SPHERE_RADIUS',     // Kure yaricapi
    CONE_ANGLE = 'CONE_ANGLE',           // Koni acisi
    CONE_PROPERTIES = 'CONE_PROPERTIES', // Koni ozellikleri
    CURVATURE = 'CURVATURE',             // Egrilik
    UNKNOWN = 'UNKNOWN'
}

/**
 * Cift secim olcum tipleri (Cetvel modu)
 */
export enum DoubleMeasurementType {
    POINT_TO_POINT = 'POINT_TO_POINT',
    POINT_TO_LINE = 'POINT_TO_LINE',
    POINT_TO_PLANE = 'POINT_TO_PLANE',
    LINE_TO_LINE_PARALLEL = 'LINE_TO_LINE_PARALLEL',
    LINE_TO_LINE_SKEW = 'LINE_TO_LINE_SKEW',
    ANGLE = 'ANGLE',
    PLANE_TO_PLANE = 'PLANE_TO_PLANE',
    DIHEDRAL_ANGLE = 'DIHEDRAL_ANGLE',
    CENTER_TO_CENTER = 'CENTER_TO_CENTER',
    MINIMUM_DISTANCE = 'MINIMUM_DISTANCE',
    UNKNOWN = 'UNKNOWN'
}

// ====================================================================
// SMART MEASURE RESULT
// ====================================================================

export interface SmartMeasureResult {
    success: boolean;
    measurementType: SingleMeasurementType | DoubleMeasurementType;
    dimensionResult?: DimensionResult;
    value?: number;
    unit?: string;
    additionalInfo?: Record<string, any>;
    error?: string;
}

// ====================================================================
// SMART MEASURE MODE CLASS
// ====================================================================

/**
 * SmartMeasureMode - Otomatik olcum tipi tespiti ve dimension olusturma
 *
 * CATIA'nin "Smart Measure" ozelligini uygular:
 * - Tek secimde: nokta=koordinat, cizgi=uzunluk, yuzey=alan, daire=cap
 * - Cift secimde: otomatik mesafe tipi, aci, minimum mesafe
 */
export class SmartMeasureMode {
    private manager: DimensionManager;

    // Toleranslar
    private readonly PARALLEL_TOLERANCE = 0.9999;
    private readonly COPLANAR_TOLERANCE = 0.001;
    private readonly COINCIDENT_TOLERANCE = 0.0001;

    // Pre-allocated vectors (zero-allocation)
    private readonly _tempVec1 = new THREE.Vector3();
    private readonly _tempVec2 = new THREE.Vector3();
    private readonly _tempVec3 = new THREE.Vector3();

    constructor(manager: DimensionManager) {
        this.manager = manager;
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * Tek secim olcumu - Kumpas modu
     * Secilen geometriye gore otomatik olcum tipi belirler
     */
    measureSingle(ref: GeometryReference): SmartMeasureResult {
        if (!ref) {
            return { success: false, measurementType: SingleMeasurementType.UNKNOWN, error: 'Referans tanimli degil' };
        }

        const measurementType = this.detectSingleMeasurementType(ref);

        switch (measurementType) {
            case SingleMeasurementType.COORDINATE:
                return this.measureCoordinate(ref);

            case SingleMeasurementType.LENGTH:
                return this.measureLength(ref);

            case SingleMeasurementType.RADIUS:
                return this.measureRadius(ref);

            case SingleMeasurementType.DIAMETER:
                return this.measureDiameter(ref);

            case SingleMeasurementType.ARC_LENGTH:
                return this.measureArcLength(ref);

            case SingleMeasurementType.ARC_PROPERTIES:
                return this.measureArcProperties(ref);

            case SingleMeasurementType.AREA:
                return this.measureArea(ref);

            case SingleMeasurementType.SPHERE_RADIUS:
                return this.measureSphereRadius(ref);

            case SingleMeasurementType.CONE_ANGLE:
            case SingleMeasurementType.CONE_PROPERTIES:
                return this.measureConeProperties(ref);

            default:
                return { success: false, measurementType, error: 'Desteklenmeyen olcum tipi' };
        }
    }

    /**
     * Cift secim olcumu - Cetvel modu
     * Iki secime gore otomatik mesafe/aci tipi belirler
     */
    measureDouble(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        if (!refA || !refB) {
            return { success: false, measurementType: DoubleMeasurementType.UNKNOWN, error: 'Referanslar tanimli degil' };
        }

        const measurementType = this.detectDoubleMeasurementType(refA, refB);

        switch (measurementType) {
            case DoubleMeasurementType.POINT_TO_POINT:
                return this.measurePointToPoint(refA, refB);

            case DoubleMeasurementType.POINT_TO_LINE:
                return this.measurePointToLine(refA, refB);

            case DoubleMeasurementType.POINT_TO_PLANE:
                return this.measurePointToPlane(refA, refB);

            case DoubleMeasurementType.LINE_TO_LINE_PARALLEL:
                return this.measureParallelLines(refA, refB);

            case DoubleMeasurementType.ANGLE:
                return this.measureAngle(refA, refB);

            case DoubleMeasurementType.PLANE_TO_PLANE:
                return this.measurePlaneToPlane(refA, refB);

            case DoubleMeasurementType.DIHEDRAL_ANGLE:
                return this.measureDihedralAngle(refA, refB);

            case DoubleMeasurementType.CENTER_TO_CENTER:
                return this.measureCenterToCenter(refA, refB);

            case DoubleMeasurementType.LINE_TO_LINE_SKEW:
            case DoubleMeasurementType.MINIMUM_DISTANCE:
            default:
                return this.measureMinimumDistance(refA, refB);
        }
    }

    // ====================================================================
    // TYPE DETECTION
    // ====================================================================

    /**
     * Tek secim icin olcum tipini tespit et
     */
    detectSingleMeasurementType(ref: GeometryReference): SingleMeasurementType {
        switch (ref.type) {
            case ReferenceType.POINT:
            case ReferenceType.VERTEX:
                return SingleMeasurementType.COORDINATE;

            case ReferenceType.EDGE:
                // Cizgi analizi
                if (this.isCircularEdge(ref)) {
                    return SingleMeasurementType.RADIUS;
                }
                if (this.isArcEdge(ref)) {
                    return SingleMeasurementType.ARC_LENGTH;
                }
                return SingleMeasurementType.LENGTH;

            case ReferenceType.FACE:
                // Yuzey analizi
                if (this.isCylindricalFace(ref)) {
                    return SingleMeasurementType.DIAMETER;
                }
                if (this.isSphericalFace(ref)) {
                    return SingleMeasurementType.SPHERE_RADIUS;
                }
                if (this.isConicalFace(ref)) {
                    return SingleMeasurementType.CONE_ANGLE;
                }
                return SingleMeasurementType.AREA;

            case ReferenceType.CIRCLE:
                return SingleMeasurementType.DIAMETER;

            case ReferenceType.ARC:
                return SingleMeasurementType.ARC_PROPERTIES;

            case ReferenceType.CYLINDER:
                return SingleMeasurementType.DIAMETER;

            case ReferenceType.CONE:
                return SingleMeasurementType.CONE_PROPERTIES;

            case ReferenceType.SPHERE:
                return SingleMeasurementType.SPHERE_RADIUS;

            default:
                return SingleMeasurementType.UNKNOWN;
        }
    }

    /**
     * Cift secim icin olcum tipini tespit et
     */
    detectDoubleMeasurementType(refA: GeometryReference, refB: GeometryReference): DoubleMeasurementType {
        const isPointA = this.isPointType(refA);
        const isPointB = this.isPointType(refB);
        const isEdgeA = refA.type === ReferenceType.EDGE;
        const isEdgeB = refB.type === ReferenceType.EDGE;
        const isFaceA = refA.type === ReferenceType.FACE;
        const isFaceB = refB.type === ReferenceType.FACE;
        const isCircularA = this.isCircularType(refA);
        const isCircularB = this.isCircularType(refB);

        // Nokta - Nokta
        if (isPointA && isPointB) {
            return DoubleMeasurementType.POINT_TO_POINT;
        }

        // Nokta - Cizgi
        if ((isPointA && isEdgeB) || (isEdgeA && isPointB)) {
            return DoubleMeasurementType.POINT_TO_LINE;
        }

        // Nokta - Yuzey
        if ((isPointA && isFaceB) || (isFaceA && isPointB)) {
            return DoubleMeasurementType.POINT_TO_PLANE;
        }

        // Cizgi - Cizgi
        if (isEdgeA && isEdgeB) {
            if (this.areParallel(refA, refB)) {
                return DoubleMeasurementType.LINE_TO_LINE_PARALLEL;
            }
            if (this.areCoplanar(refA, refB)) {
                return DoubleMeasurementType.ANGLE;
            }
            return DoubleMeasurementType.LINE_TO_LINE_SKEW;
        }

        // Yuzey - Yuzey
        if (isFaceA && isFaceB) {
            if (this.arePlanesParallel(refA, refB)) {
                return DoubleMeasurementType.PLANE_TO_PLANE;
            }
            return DoubleMeasurementType.DIHEDRAL_ANGLE;
        }

        // Dairesel - Dairesel (merkez mesafesi)
        if (isCircularA && isCircularB) {
            return DoubleMeasurementType.CENTER_TO_CENTER;
        }

        // Genel durum - Minimum mesafe
        return DoubleMeasurementType.MINIMUM_DISTANCE;
    }

    // ====================================================================
    // SINGLE MEASUREMENT IMPLEMENTATIONS
    // ====================================================================

    private measureCoordinate(ref: GeometryReference): SmartMeasureResult {
        const point = ref.point || ref.center;
        if (!point) {
            return { success: false, measurementType: SingleMeasurementType.COORDINATE, error: 'Nokta bulunamadi' };
        }

        return {
            success: true,
            measurementType: SingleMeasurementType.COORDINATE,
            additionalInfo: {
                x: point.x,
                y: point.y,
                z: point.z,
                formatted: `X: ${point.x.toFixed(3)}, Y: ${point.y.toFixed(3)}, Z: ${point.z.toFixed(3)}`
            }
        };
    }

    private measureLength(ref: GeometryReference): SmartMeasureResult {
        // Kenar uzunlugu hesapla
        let length = 0;

        if (ref.mesh && ref.edgeIndex !== undefined) {
            // Mesh'ten kenar uzunlugu
            const geometry = ref.mesh.geometry;
            const positions = geometry.getAttribute('position');

            if (positions) {
                // Edge vertices'ten uzunluk hesapla
                // Bu basit bir yaklasiim, gercek implementasyonda edge topology kullanilmali
                length = this.calculateEdgeLength(ref);
            }
        }

        return {
            success: true,
            measurementType: SingleMeasurementType.LENGTH,
            value: length,
            unit: 'mm',
            additionalInfo: {
                direction: ref.direction?.clone()
            }
        };
    }

    private measureRadius(ref: GeometryReference): SmartMeasureResult {
        const radius = ref.radius;
        if (radius === undefined || radius === null) {
            return { success: false, measurementType: SingleMeasurementType.RADIUS, error: 'Yaricap bulunamadi' };
        }

        // Dimension olustur
        const result = this.manager.createRadiusDimension(ref);

        return {
            success: result.success,
            measurementType: SingleMeasurementType.RADIUS,
            dimensionResult: result,
            value: radius,
            unit: 'mm',
            additionalInfo: {
                center: ref.center?.clone()
            }
        };
    }

    private measureDiameter(ref: GeometryReference): SmartMeasureResult {
        const radius = ref.radius;
        if (radius === undefined || radius === null) {
            return { success: false, measurementType: SingleMeasurementType.DIAMETER, error: 'Yaricap bulunamadi' };
        }

        // Diameter dimension olustur
        const result = this.manager.createDiameterDimension(ref);

        return {
            success: result.success,
            measurementType: SingleMeasurementType.DIAMETER,
            dimensionResult: result,
            value: radius * 2,
            unit: 'mm',
            additionalInfo: {
                radius: radius,
                center: ref.center?.clone()
            }
        };
    }

    private measureArcLength(ref: GeometryReference): SmartMeasureResult {
        const radius = ref.radius;
        const startAngle = ref.startAngle;
        const endAngle = ref.endAngle;

        if (radius === undefined || startAngle === undefined || endAngle === undefined) {
            return { success: false, measurementType: SingleMeasurementType.ARC_LENGTH, error: 'Yay parametreleri eksik' };
        }

        // Yay uzunlugu = R * theta
        const theta = Math.abs(endAngle - startAngle);
        const arcLength = radius * theta;

        return {
            success: true,
            measurementType: SingleMeasurementType.ARC_LENGTH,
            value: arcLength,
            unit: 'mm',
            additionalInfo: {
                radius: radius,
                startAngle: startAngle * (180 / Math.PI),
                endAngle: endAngle * (180 / Math.PI),
                sweepAngle: theta * (180 / Math.PI)
            }
        };
    }

    private measureArcProperties(ref: GeometryReference): SmartMeasureResult {
        const arcResult = this.measureArcLength(ref);
        if (!arcResult.success) {
            return arcResult;
        }

        return {
            success: true,
            measurementType: SingleMeasurementType.ARC_PROPERTIES,
            value: arcResult.value,
            unit: 'mm',
            additionalInfo: {
                ...arcResult.additionalInfo,
                center: ref.center?.clone(),
                axis: ref.axis?.clone()
            }
        };
    }

    private measureArea(ref: GeometryReference): SmartMeasureResult {
        let area = 0;

        if (ref.mesh) {
            area = this.calculateMeshArea(ref.mesh, ref.faceIndex);
        }

        return {
            success: true,
            measurementType: SingleMeasurementType.AREA,
            value: area,
            unit: 'mm\u00B2',
            additionalInfo: {
                centroid: this.calculateCentroid(ref.mesh, ref.faceIndex),
                normal: ref.normal?.clone()
            }
        };
    }

    private measureSphereRadius(ref: GeometryReference): SmartMeasureResult {
        const radius = ref.radius;
        if (radius === undefined || radius === null) {
            return { success: false, measurementType: SingleMeasurementType.SPHERE_RADIUS, error: 'Kure yaricapi bulunamadi' };
        }

        return {
            success: true,
            measurementType: SingleMeasurementType.SPHERE_RADIUS,
            value: radius,
            unit: 'mm',
            additionalInfo: {
                center: ref.center?.clone(),
                surfaceArea: 4 * Math.PI * radius * radius,
                volume: (4 / 3) * Math.PI * radius * radius * radius
            }
        };
    }

    private measureConeProperties(ref: GeometryReference): SmartMeasureResult {
        // Koni ozellikleri
        // halfAngle, apex, axis bilgileri kullanilir
        return {
            success: true,
            measurementType: SingleMeasurementType.CONE_PROPERTIES,
            additionalInfo: {
                center: ref.center?.clone(),
                axis: ref.axis?.clone(),
                radius: ref.radius
            }
        };
    }

    // ====================================================================
    // DOUBLE MEASUREMENT IMPLEMENTATIONS
    // ====================================================================

    private measurePointToPoint(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        const pointA = refA.point || refA.center;
        const pointB = refB.point || refB.center;

        if (!pointA || !pointB) {
            return { success: false, measurementType: DoubleMeasurementType.POINT_TO_POINT, error: 'Noktalar bulunamadi' };
        }

        const distance = pointA.distanceTo(pointB);

        // Linear dimension olustur
        const result = this.manager.createLinearDimension(refA, refB);

        // Delta degerlerini hesapla
        this._tempVec1.subVectors(pointB, pointA);

        return {
            success: result.success,
            measurementType: DoubleMeasurementType.POINT_TO_POINT,
            dimensionResult: result,
            value: distance,
            unit: 'mm',
            additionalInfo: {
                pointA: pointA.clone(),
                pointB: pointB.clone(),
                deltaX: this._tempVec1.x,
                deltaY: this._tempVec1.y,
                deltaZ: this._tempVec1.z,
                distanceXY: Math.sqrt(this._tempVec1.x ** 2 + this._tempVec1.y ** 2),
                distanceXZ: Math.sqrt(this._tempVec1.x ** 2 + this._tempVec1.z ** 2),
                distanceYZ: Math.sqrt(this._tempVec1.y ** 2 + this._tempVec1.z ** 2)
            }
        };
    }

    private measurePointToLine(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        // Hangi referans nokta, hangi cizgi?
        const [pointRef, lineRef] = this.isPointType(refA) ? [refA, refB] : [refB, refA];

        const point = pointRef.point || pointRef.center;
        const lineStart = lineRef.point;
        const lineDir = lineRef.direction;

        if (!point || !lineStart || !lineDir) {
            return { success: false, measurementType: DoubleMeasurementType.POINT_TO_LINE, error: 'Parametreler eksik' };
        }

        // Noktanin cizgiye dik mesafesi
        this._tempVec1.subVectors(point, lineStart);
        const projection = this._tempVec1.clone().projectOnVector(lineDir);
        const perpendicular = this._tempVec1.clone().sub(projection);
        const distance = perpendicular.length();

        // En yakin nokta
        const closestPoint = lineStart.clone().add(projection);

        return {
            success: true,
            measurementType: DoubleMeasurementType.POINT_TO_LINE,
            value: distance,
            unit: 'mm',
            additionalInfo: {
                point: point.clone(),
                closestPointOnLine: closestPoint,
                perpendicularDistance: distance
            }
        };
    }

    private measurePointToPlane(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        // Hangi referans nokta, hangi yuzey?
        const [pointRef, planeRef] = this.isPointType(refA) ? [refA, refB] : [refB, refA];

        const point = pointRef.point || pointRef.center;
        const planePoint = planeRef.point || planeRef.center;
        const planeNormal = planeRef.normal;

        if (!point || !planePoint || !planeNormal) {
            return { success: false, measurementType: DoubleMeasurementType.POINT_TO_PLANE, error: 'Parametreler eksik' };
        }

        // Noktanin duzleme mesafesi
        this._tempVec1.subVectors(point, planePoint);
        const signedDistance = this._tempVec1.dot(planeNormal);
        const distance = Math.abs(signedDistance);

        // En yakin nokta
        const closestPoint = point.clone().addScaledVector(planeNormal, -signedDistance);

        return {
            success: true,
            measurementType: DoubleMeasurementType.POINT_TO_PLANE,
            value: distance,
            unit: 'mm',
            additionalInfo: {
                point: point.clone(),
                closestPointOnPlane: closestPoint,
                signedDistance: signedDistance,
                perpendicularDistance: distance
            }
        };
    }

    private measureParallelLines(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        const pointA = refA.point;
        const pointB = refB.point;
        const dirA = refA.direction;

        if (!pointA || !pointB || !dirA) {
            return { success: false, measurementType: DoubleMeasurementType.LINE_TO_LINE_PARALLEL, error: 'Parametreler eksik' };
        }

        // Paralel cizgiler arasi mesafe
        this._tempVec1.subVectors(pointB, pointA);
        const projection = this._tempVec1.clone().projectOnVector(dirA);
        const perpendicular = this._tempVec1.clone().sub(projection);
        const distance = perpendicular.length();

        return {
            success: true,
            measurementType: DoubleMeasurementType.LINE_TO_LINE_PARALLEL,
            value: distance,
            unit: 'mm',
            additionalInfo: {
                parallel: true,
                parallelDistance: distance
            }
        };
    }

    private measureAngle(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        const dirA = refA.direction;
        const dirB = refB.direction;

        if (!dirA || !dirB) {
            return { success: false, measurementType: DoubleMeasurementType.ANGLE, error: 'Yon vektorleri eksik' };
        }

        // Aci hesapla
        const dot = dirA.dot(dirB);
        const angle = Math.acos(Math.abs(dot)) * (180 / Math.PI);

        // Angular dimension olustur
        const result = this.manager.createAngleDimension(refA, refB);

        return {
            success: result.success,
            measurementType: DoubleMeasurementType.ANGLE,
            dimensionResult: result,
            value: angle,
            unit: '\u00B0',
            additionalInfo: {
                angleRadians: Math.acos(Math.abs(dot)),
                complementary: 180 - angle
            }
        };
    }

    private measurePlaneToPlane(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        const pointA = refA.point || refA.center;
        const pointB = refB.point || refB.center;
        const normalA = refA.normal;

        if (!pointA || !pointB || !normalA) {
            return { success: false, measurementType: DoubleMeasurementType.PLANE_TO_PLANE, error: 'Parametreler eksik' };
        }

        // Paralel duzlemler arasi mesafe
        this._tempVec1.subVectors(pointB, pointA);
        const distance = Math.abs(this._tempVec1.dot(normalA));

        return {
            success: true,
            measurementType: DoubleMeasurementType.PLANE_TO_PLANE,
            value: distance,
            unit: 'mm',
            additionalInfo: {
                parallel: true,
                parallelDistance: distance
            }
        };
    }

    private measureDihedralAngle(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        const normalA = refA.normal;
        const normalB = refB.normal;

        if (!normalA || !normalB) {
            return { success: false, measurementType: DoubleMeasurementType.DIHEDRAL_ANGLE, error: 'Normal vektorler eksik' };
        }

        // Dihedral aci
        const dot = normalA.dot(normalB);
        const angle = Math.acos(Math.abs(dot)) * (180 / Math.PI);

        return {
            success: true,
            measurementType: DoubleMeasurementType.DIHEDRAL_ANGLE,
            value: angle,
            unit: '\u00B0',
            additionalInfo: {
                dihedralAngle: angle,
                angleRadians: Math.acos(Math.abs(dot))
            }
        };
    }

    private measureCenterToCenter(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        const centerA = refA.center;
        const centerB = refB.center;

        if (!centerA || !centerB) {
            return { success: false, measurementType: DoubleMeasurementType.CENTER_TO_CENTER, error: 'Merkezler bulunamadi' };
        }

        const centerDistance = centerA.distanceTo(centerB);

        // Kenar mesafesi (gap/interference)
        const radiusA = refA.radius || 0;
        const radiusB = refB.radius || 0;
        const edgeDistance = centerDistance - radiusA - radiusB;

        return {
            success: true,
            measurementType: DoubleMeasurementType.CENTER_TO_CENTER,
            value: centerDistance,
            unit: 'mm',
            additionalInfo: {
                centerA: centerA.clone(),
                centerB: centerB.clone(),
                centerDistance: centerDistance,
                edgeDistance: edgeDistance,
                interference: edgeDistance < 0
            }
        };
    }

    private measureMinimumDistance(refA: GeometryReference, refB: GeometryReference): SmartMeasureResult {
        // Genel minimum mesafe hesaplama
        // Bu GPU accelerated olabilir

        // Basit nokta-nokta yaklaşımı (gercek implementasyonda GPU kullanilmali)
        const pointA = refA.point || refA.center;
        const pointB = refB.point || refB.center;

        if (pointA && pointB) {
            const distance = pointA.distanceTo(pointB);

            return {
                success: true,
                measurementType: DoubleMeasurementType.MINIMUM_DISTANCE,
                value: distance,
                unit: 'mm',
                additionalInfo: {
                    closestPointA: pointA.clone(),
                    closestPointB: pointB.clone(),
                    minimumDistance: distance
                }
            };
        }

        return {
            success: false,
            measurementType: DoubleMeasurementType.MINIMUM_DISTANCE,
            error: 'Minimum mesafe hesaplanamadi'
        };
    }

    // ====================================================================
    // HELPER METHODS
    // ====================================================================

    private isPointType(ref: GeometryReference): boolean {
        return ref.type === ReferenceType.POINT || ref.type === ReferenceType.VERTEX;
    }

    private isCircularType(ref: GeometryReference): boolean {
        return ref.type === ReferenceType.CIRCLE ||
               ref.type === ReferenceType.ARC ||
               ref.type === ReferenceType.CYLINDER ||
               ref.type === ReferenceType.SPHERE;
    }

    private isCircularEdge(ref: GeometryReference): boolean {
        // Kenar tam daire mi kontrol et
        if (ref.startAngle !== undefined && ref.endAngle !== undefined) {
            const sweep = Math.abs(ref.endAngle - ref.startAngle);
            return sweep >= Math.PI * 1.99; // ~360 derece
        }
        return false;
    }

    private isArcEdge(ref: GeometryReference): boolean {
        return ref.startAngle !== undefined && ref.endAngle !== undefined && !this.isCircularEdge(ref);
    }

    private isCylindricalFace(ref: GeometryReference): boolean {
        return ref.type === ReferenceType.CYLINDER ||
               (ref.type === ReferenceType.FACE && ref.radius !== undefined && ref.axis !== undefined);
    }

    private isSphericalFace(ref: GeometryReference): boolean {
        return ref.type === ReferenceType.SPHERE;
    }

    private isConicalFace(ref: GeometryReference): boolean {
        return ref.type === ReferenceType.CONE;
    }

    private areParallel(refA: GeometryReference, refB: GeometryReference): boolean {
        if (!refA.direction || !refB.direction) return false;
        const dot = Math.abs(refA.direction.dot(refB.direction));
        return dot > this.PARALLEL_TOLERANCE;
    }

    private areCoplanar(refA: GeometryReference, refB: GeometryReference): boolean {
        if (!refA.point || !refB.point || !refA.direction || !refB.direction) return false;

        // Cizgiler arasi vektor
        this._tempVec1.subVectors(refB.point, refA.point);

        // Cross product ile normal
        this._tempVec2.crossVectors(refA.direction, refB.direction);

        // Eger cizgiler arasi vektor, normal'a dik ise coplanar
        const dot = Math.abs(this._tempVec1.dot(this._tempVec2.normalize()));
        return dot < this.COPLANAR_TOLERANCE;
    }

    private arePlanesParallel(refA: GeometryReference, refB: GeometryReference): boolean {
        if (!refA.normal || !refB.normal) return false;
        const dot = Math.abs(refA.normal.dot(refB.normal));
        return dot > this.PARALLEL_TOLERANCE;
    }

    private calculateEdgeLength(ref: GeometryReference): number {
        // Basit kenar uzunlugu hesaplama
        // Gercek implementasyonda edge topology kullanilmali
        if (ref.mesh && ref.mesh.geometry) {
            const geometry = ref.mesh.geometry;
            const positions = geometry.getAttribute('position');
            if (!positions) return 0;

            let length = 0;
            const v1 = new THREE.Vector3();
            const v2 = new THREE.Vector3();

            for (let i = 1; i < positions.count; i++) {
                v1.fromBufferAttribute(positions, i - 1);
                v2.fromBufferAttribute(positions, i);
                length += v1.distanceTo(v2);
            }

            return length;
        }
        return 0;
    }

    private calculateMeshArea(mesh: THREE.Mesh, faceIndex?: number): number {
        if (!mesh || !mesh.geometry) return 0;

        const geometry = mesh.geometry;
        const positions = geometry.getAttribute('position');
        const indices = geometry.getIndex();

        if (!positions) return 0;

        let totalArea = 0;
        const v0 = new THREE.Vector3();
        const v1 = new THREE.Vector3();
        const v2 = new THREE.Vector3();
        const ab = new THREE.Vector3();
        const ac = new THREE.Vector3();

        if (indices) {
            // Indexed geometry
            const startIdx = faceIndex !== undefined ? faceIndex * 3 : 0;
            const endIdx = faceIndex !== undefined ? startIdx + 3 : indices.count;

            for (let i = startIdx; i < endIdx; i += 3) {
                v0.fromBufferAttribute(positions, indices.getX(i));
                v1.fromBufferAttribute(positions, indices.getX(i + 1));
                v2.fromBufferAttribute(positions, indices.getX(i + 2));

                v0.applyMatrix4(mesh.matrixWorld);
                v1.applyMatrix4(mesh.matrixWorld);
                v2.applyMatrix4(mesh.matrixWorld);

                ab.subVectors(v1, v0);
                ac.subVectors(v2, v0);
                totalArea += ab.cross(ac).length() / 2;
            }
        } else {
            // Non-indexed geometry
            for (let i = 0; i < positions.count; i += 3) {
                v0.fromBufferAttribute(positions, i);
                v1.fromBufferAttribute(positions, i + 1);
                v2.fromBufferAttribute(positions, i + 2);

                v0.applyMatrix4(mesh.matrixWorld);
                v1.applyMatrix4(mesh.matrixWorld);
                v2.applyMatrix4(mesh.matrixWorld);

                ab.subVectors(v1, v0);
                ac.subVectors(v2, v0);
                totalArea += ab.cross(ac).length() / 2;
            }
        }

        return totalArea;
    }

    private calculateCentroid(mesh?: THREE.Mesh, faceIndex?: number): THREE.Vector3 | null {
        if (!mesh || !mesh.geometry) return null;

        const geometry = mesh.geometry;
        const positions = geometry.getAttribute('position');

        if (!positions) return null;

        const centroid = new THREE.Vector3();
        let count = 0;

        for (let i = 0; i < positions.count; i++) {
            centroid.x += positions.getX(i);
            centroid.y += positions.getY(i);
            centroid.z += positions.getZ(i);
            count++;
        }

        if (count > 0) {
            centroid.divideScalar(count);
            centroid.applyMatrix4(mesh.matrixWorld);
        }

        return centroid;
    }
}
